import {
  useCallback,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ChangeEvent,
} from 'react'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { getNlsString } from '../../nls/nlsRuntime'
import { formatFileSize } from './fileRowHelpers'
import { partitionFiles, type FileReject } from './filePickerHelpers'

export type { FileReject }

/* ---------------------------------------------------------------------------
 * Default hint builder
 * ---------------------------------------------------------------------------*/

function buildDefaultHint(accept: string, maxBytes: number): string {
  const tokens = accept
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  const exts = tokens
    .filter((t) => t.startsWith('.'))
    .map((t) => t.slice(1).toUpperCase())

  const extPart = exts.length > 0 ? exts.join(', ') + ' · ' : ''
  const upTo = getNlsString('NLSCommonFilePickerHintUpTo', { size: formatFileSize(maxBytes) })
  return extPart + upTo
}

/* ---------------------------------------------------------------------------
 * Props
 * ---------------------------------------------------------------------------*/

export type FilePickerProps = {
  id?: string
  dataTestId?: string
  accept: string
  maxBytes: number
  multiple?: boolean
  disabled?: boolean
  onFiles: (files: File[]) => void
  onReject?: (rejects: FileReject[]) => void
  hint?: string
}

/* ---------------------------------------------------------------------------
 * FilePicker
 * ---------------------------------------------------------------------------*/

export function FilePicker({
  id,
  dataTestId,
  accept,
  maxBytes,
  multiple = true,
  disabled = false,
  onFiles,
  onReject,
  hint,
}: FilePickerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragover, setDragover] = useState(false)
  const [rejectMessages, setRejectMessages] = useState<string[]>([])

  const processFiles = useCallback(
    (rawFiles: File[]) => {
      const { accepted, rejected } = partitionFiles(rawFiles, accept, maxBytes, multiple)
      if (accepted.length > 0) onFiles(accepted)
      if (rejected.length > 0) {
        onReject?.(rejected)
        setRejectMessages(
          rejected.map(({ file, reason }) => {
            const key =
              reason === 'size'
                ? 'NLSCommonFilePickerRejectSize'
                : 'NLSCommonFilePickerRejectType'
            return getNlsString(key).replace('{name}', file.name)
          }),
        )
      } else {
        setRejectMessages([])
      }
    },
    [accept, maxBytes, multiple, onFiles, onReject],
  )

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click()
  }, [disabled])

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        inputRef.current?.click()
      }
    },
    [],
  )

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (files.length > 0) processFiles(files)
      // Reset input so the same file can be picked again
      e.target.value = ''
    },
    [processFiles],
  )

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (disabled) return
      e.preventDefault()
      setDragover(true)
    },
    [disabled],
  )

  const handleDragLeave = useCallback(() => {
    setDragover(false)
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (disabled) return
      e.preventDefault()
      setDragover(false)
      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) processFiles(files)
    },
    [disabled, processFiles],
  )

  const resolvedHint = hint ?? buildDefaultHint(accept, maxBytes)

  return (
    <div>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        id={id}
        data-testid={dataTestId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={handleChange}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Dropzone */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        className={[
          'dsDropzone',
          dragover ? 'is-dragover' : '',
          disabled ? 'is-disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span className="dsDropzone__icon">
          <UploadFileRoundedIcon fontSize="inherit" />
        </span>
        <span className="dsDropzone__title">
          {(() => {
            const title = getNlsString('NLSCommonFilePickerDropzoneTitle')
            const browseWord = getNlsString('NLSCommonFilePickerDropzoneBrowse')
            const parts = title.split('{browse}')
            if (parts.length === 2) {
              return <>{parts[0]}<b>{browseWord}</b>{parts[1]}</>
            }
            // Fallback: no placeholder found — append bold word after
            return <>{title} <b>{browseWord}</b></>
          })()}
        </span>
        <span className="dsDropzone__hint">{resolvedHint}</span>
      </div>

      {/* Reject messages */}
      {rejectMessages.length > 0 && (
        <div
          role="alert"
          style={{ marginTop: '6px', fontSize: '11px', color: 'color-mix(in srgb, var(--danger) 60%, var(--text-strong))' }}
        >
          {rejectMessages.join(' ')}
        </div>
      )}
    </div>
  )
}
